import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Button from '../components/formElements/Button';
import { useProducer } from '../context/ProducerContext';
import { Producer } from '../models/Producer';

export interface ProducerFormData {
  name: string;
  description?: string;
  foundedYear?: string;
  headquarters?: string;
  website?: string;
  speciality?: string;
  history?: string;
  image?: File | null;
}

interface ProducerFormProps {
  producer?: Producer;
  inEditMode?: boolean;
}

function ProducerForm({ producer, inEditMode = false }: ProducerFormProps) {
  const [imagePreview, setImagePreview] = useState<string | null>(
    producer?.image || null,
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<ProducerFormData>({
    defaultValues: {
      name: producer?.name || '',
      description: producer?.description || '',
      foundedYear: producer?.foundedYear?.toString() || '',
      headquarters: producer?.headquarters || '',
      website: producer?.website || '',
      speciality: producer?.speciality || '',
      history: producer?.history || '',
      image: null,
    },
  });

  const { createProducer, editProducer } = useProducer();
  const navigate = useNavigate();

  useEffect(() => {
    if (producer?.image) {
      setValue('image', null);
      setImagePreview(producer?.image);
    }
  }, [producer?.image, setValue]);

  const handleImageChange = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(producer?.image || null);
    }

    setValue('image', file);
  };

  const onSubmit = async (data: ProducerFormData) => {
    const toastId = toast.loading(
      inEditMode ? 'Edytowanie...' : 'Dodawanie...',
      {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
        theme: 'colored',
      },
    );

    try {
      if (inEditMode && producer?.id) {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });
        await editProducer(producer?.id, formData);
        toast.update(toastId, {
          render: 'Producent edytowany!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });
        await createProducer(formData);
        toast.update(toastId, {
          render: 'Producent dodany!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      }

      navigate('/admin/producenci');
    } catch (error: Error | any) {
      console.error('Failed to process producer', error);
      toast.update(toastId, {
        render: inEditMode
          ? 'Nie udało się edytować producenta'
          : 'Nie udało się dodać producenta',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white rounded-lg shadow-md p-6"
    >
      <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-gray-200">
        {inEditMode ? 'Edycja producenta' : 'Dodaj nowego producenta'}
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pierwsza kolumna - Zdjęcie */}
        <div className="lg:col-span-1">
          <div className="flex flex-col items-center">
            <div className="w-full mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo producenta
              </label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                style={{ minHeight: '300px' }}
              >
                <Controller
                  name="image"
                  control={control}
                  defaultValue={null}
                  render={({ field }) => (
                    <>
                      <input
                        id="image-input"
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleImageChange(e.target.files?.[0] || null)
                        }
                        className="hidden"
                      />
                      <label
                        htmlFor="image-input"
                        className="cursor-pointer w-full h-full flex flex-col items-center justify-center"
                      >
                        {imagePreview ? (
                          <div className="relative w-full h-full flex items-center justify-center">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="max-w-full max-h-64 object-contain"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                              <span className="text-white opacity-0 hover:opacity-100 bg-black bg-opacity-50 px-3 py-1 rounded">
                                Zmień zdjęcie
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <p className="mt-2 text-sm text-gray-500">
                              Kliknij aby dodać zdjęcie producenta
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              PNG, JPG, GIF do 5MB
                            </p>
                          </div>
                        )}
                      </label>
                      {errors.image && (
                        <p className="text-red-500 text-sm mt-1">
                          {String(errors.image.message)}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Druga i trzecia kolumna - Dane podstawowe i dodatkowe */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Dane podstawowe */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 mb-3">
                Podstawowe informacje
              </h3>

              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Nazwa producenta *
                </label>
                <Controller
                  name="name"
                  control={control}
                  defaultValue={producer?.name}
                  rules={{ required: 'Nazwa jest wymagana' }}
                  render={({ field }) => (
                    <div>
                      <input
                        {...field}
                        id="name"
                        className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
                        placeholder="np. Samsung, Apple"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">
                          {String(errors.name.message)}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>

              <div>
                <label
                  htmlFor="founded"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Rok założenia
                </label>
                <Controller
                  name="foundedYear"
                  control={control}
                  defaultValue={producer?.foundedYear?.toString()}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="foundedYear"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="np. 1976"
                    />
                  )}
                />
              </div>

              <div>
                <label
                  htmlFor="headquarters"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Siedziba
                </label>
                <Controller
                  name="headquarters"
                  control={control}
                  defaultValue={producer?.headquarters}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="headquarters"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="np. Warszawa, Polska"
                    />
                  )}
                />
              </div>

              <div>
                <label
                  htmlFor="website"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Strona WWW
                </label>
                <Controller
                  name="website"
                  control={control}
                  defaultValue={producer?.website}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="website"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="np. https://www.samsung.com"
                    />
                  )}
                />
              </div>
            </div>

            {/* Dane dodatkowe */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 mb-3">
                Szczegółowe informacje
              </h3>

              <div>
                <label
                  htmlFor="speciality"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Specjalizacja
                </label>
                <Controller
                  name="speciality"
                  control={control}
                  defaultValue={producer?.speciality}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="speciality"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="np. Elektronika konsumencka"
                    />
                  )}
                />
              </div>

              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Krótki opis
                </label>
                <Controller
                  name="description"
                  control={control}
                  defaultValue={producer?.description || ''}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      id="description"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Krótki opis producenta"
                    />
                  )}
                />
              </div>

              <div>
                <label
                  htmlFor="history"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Historia firmy
                </label>
                <Controller
                  name="history"
                  control={control}
                  defaultValue={producer?.history}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      id="history"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Szczegółowa historia działalności firmy"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Przyciski formularza */}
      <div className="mt-6 flex justify-end space-x-4">
        <Button
          type="button"
          colorTheme="outline"
          onClick={() => navigate('/admin/producenci')}
          className="w-auto px-6"
          size="small"
        >
          Anuluj
        </Button>
        <Button
          type="submit"
          colorTheme="primary"
          className="w-auto px-6"
          size="small"
        >
          {inEditMode ? 'Zaktualizuj producenta' : 'Utwórz producenta'}
        </Button>
      </div>
    </form>
  );
}

export default ProducerForm;
